import { useState, useEffect, useRef, useMemo } from 'react';
import {
  COOWN_ADMIN_BUILD,
  SUBMISSION_PUBLISH_STATUSES,
  FEATURED_MARKET_CODES,
  ADMIN_LIVE_LISTINGS_PAGE_SIZE,
  COOWN_PROJECT_START_YEAR,
} from '../config/constants';
import {
  formatUsd,
  formatExactMoney,
  adminLiveListingMatchesSearch,
  propertyManagerFromRecord,
  getPropertyListingMode,
} from '../data/countries';
import {
  getPropertyPlatformPrice,
  getPropertyListerPrice,
  isFirestoreListingId,
  buildAdminLiveListingCatalog,
  getPropertyListedAt,
} from '../utils/properties';
import {
  listingMatchesListedDateFilter,
  downloadLiveListingsCsv,
  LIVE_LISTINGS_METRICS,
  LIVE_LISTINGS_CHART_COLORS,
  computeLiveListingsMetrics,
} from '../utils/adminLiveListings';
import { submissionMatchesAdminDateFilter } from '../utils/adminDateFilter';
import PlatformActivityChart from '../components/admin/PlatformActivityChart';
import BrowseListedDateFilter from '../components/listings/BrowseListedDateFilter';
import {
  classifySubmission,
  getSubmissionListingMode,
  formatSubmissionListingModeLabel,
  submissionMatchesSearch,
  submissionSortTime,
  formatSubmissionStatusLabel,
  submissionStatusBadgeClass,
  normalizePipelineStatus,
  formatShareStructureSummary,
  saleListingNeedsRaisingApproval,
  getPipelineStepLabel,
} from '../utils/submissions';
import apiClient from '../utils/api';
import {
  assertValidAdminPin,
  adminFirestoreGetPinHash,
  adminPipelineViaFirestore,
} from '../utils/admin';
import useListPagination from '../hooks/useListPagination';
import useAdminChatReportNotifications from '../hooks/useAdminChatReportNotifications';
import useAdminDocumentNotifications from '../hooks/useAdminDocumentNotifications';
import AdminPinModal from '../components/admin/AdminPinModal';
import AdminSubmissionDetail from '../components/admin/AdminSubmissionDetail';
import AdminAddPropertyPanel from '../components/admin/AdminAddPropertyPanel';
import AdminDeletedListingsPanel from '../components/admin/AdminDeletedListingsPanel';
import AdminInsightsPanel from '../components/admin/AdminInsightsPanel';
import AdminLegalPagesPanel from '../components/admin/AdminLegalPagesPanel';
import AdminKycReviewPanel from '../components/admin/AdminKycReviewPanel';
import AdminChatReportsPanel from '../components/admin/AdminChatReportsPanel';
import AdminPlatformOverview from '../components/admin/AdminPlatformOverview';
import AdminDistributionPanel from '../components/admin/AdminDistributionPanel';
import AdminUserAccessPanel from '../components/admin/AdminUserAccessPanel';
import AdminPropertyManageModal from '../components/admin/AdminPropertyManageModal';
import AdminPropertyDocumentsPanel from '../components/admin/AdminPropertyDocumentsPanel';
import PaginationBar from '../components/shared/PaginationBar';

const AdminDashboardPage = ({ properties, catalogProperties, investments, isAdmin, isLoggedIn, adminCheckPending, userEmail, onPropertyCreated, onGoHome, onGoLogin, addToast }) => {
  const [activeTab, setActiveTab] = useState("submissions");
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const [hasAdminPin, setHasAdminPin] = useState(false);
  const [pinModal, setPinModal] = useState({ open: false, mode: "set" });
  const [pendingApproval, setPendingApproval] = useState(null);
  const [submissionQueueFilter, setSubmissionQueueFilter] = useState("pending");
  const [submissionListingModeFilter, setSubmissionListingModeFilter] = useState("all");
  const [submissionSearchQuery, setSubmissionSearchQuery] = useState("");
  const [submissionDateMode, setSubmissionDateMode] = useState("all");
  const [submissionDateYear, setSubmissionDateYear] = useState("");
  const [submissionDateMonth, setSubmissionDateMonth] = useState("");
  const [submissionDateDay, setSubmissionDateDay] = useState("");
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [focusedSubmissionId, setFocusedSubmissionId] = useState(null);
  const [pendingSubmissionReview, setPendingSubmissionReview] = useState(null);
  const [pipelineBusy, setPipelineBusy] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [raisingSaveBusy, setRaisingSaveBusy] = useState(false);
  const [offeringSaveBusy, setOfferingSaveBusy] = useState(false);
  const [contentSaveBusy, setContentSaveBusy] = useState(false);
  const offeringDraftRef = useRef({});
  const [deletedCount, setDeletedCount] = useState(0);
  const [manageProperty, setManageProperty] = useState(null);
  const [liveListingsSearch, setLiveListingsSearch] = useState("");
  const [liveListingsModeFilter, setLiveListingsModeFilter] = useState("all");
  const [liveListingsDateMode, setLiveListingsDateMode] = useState("all");
  const [liveListingsMonth, setLiveListingsMonth] = useState("");
  const [liveListingsDate, setLiveListingsDate] = useState("");
  const [liveListingsYear, setLiveListingsYear] = useState("");
  const [liveListingsExporting, setLiveListingsExporting] = useState(false);
  const [liveListingsChartType, setLiveListingsChartType] = useState('pie');
  const { openCount: chatReportsOpenCount, refreshOpenCount: refreshChatReportCount } = useAdminChatReportNotifications({
    enabled: Boolean(isAdmin && isLoggedIn),
    addToast,
    suppressAlerts: activeTab === 'chat-reports',
  });
  const { openCount: documentsOpenCount, refreshOpenCount: refreshDocumentQueueCount } = useAdminDocumentNotifications({
    enabled: Boolean(isAdmin && isLoggedIn),
    addToast,
    suppressAlerts: activeTab === 'documents',
  });
  const allLiveListings = useMemo(
    () => buildAdminLiveListingCatalog(catalogProperties, properties),
    [catalogProperties, properties]
  );
  const liveListingModeCounts = useMemo(() => ({
    all: allLiveListings.length,
    sale: allLiveListings.filter((d) => getPropertyListingMode(d) === "sale").length,
    rent: allLiveListings.filter((d) => getPropertyListingMode(d) === "rent").length,
  }), [allLiveListings]);
  const filteredLiveListings = useMemo(
    () => allLiveListings.filter((d) => {
      if (liveListingsModeFilter !== "all" && getPropertyListingMode(d) !== liveListingsModeFilter) return false;
      if (!listingMatchesListedDateFilter(d, {
        mode: liveListingsDateMode,
        year: liveListingsYear,
        month: liveListingsMonth,
        date: liveListingsDate,
      })) return false;
      return adminLiveListingMatchesSearch(d, liveListingsSearch);
    }),
    [allLiveListings, liveListingsModeFilter, liveListingsSearch, liveListingsDateMode, liveListingsYear, liveListingsMonth, liveListingsDate]
  );
  const liveListingsPaginationKey = `admin-deals|${liveListingsModeFilter}|${liveListingsSearch}|${liveListingsDateMode}|${liveListingsYear}|${liveListingsMonth}|${liveListingsDate}`;
  const {
    items: liveListingsPage,
    page: liveListingsPageNum,
    total: liveListingsTotal,
    totalPages: liveListingsTotalPages,
    startIndex: liveListingsStart,
    endIndex: liveListingsEnd,
    hasPrev: liveListingsHasPrev,
    hasNext: liveListingsHasNext,
    goToPage: goToLiveListingsPage
  } = useListPagination(filteredLiveListings, liveListingsPaginationKey, ADMIN_LIVE_LISTINGS_PAGE_SIZE);
  const liveListingsMetrics = useMemo(
    () => computeLiveListingsMetrics(filteredLiveListings),
    [filteredLiveListings]
  );
  const submissionDateFilter = useMemo(() => ({
    mode: submissionDateMode,
    year: submissionDateYear,
    month: submissionDateMonth,
    date: submissionDateDay,
  }), [submissionDateMode, submissionDateYear, submissionDateMonth, submissionDateDay]);
  const liveListingsFiltersActive = liveListingsSearch.trim() !== ''
    || liveListingsModeFilter !== 'all'
    || liveListingsDateMode !== 'all';
  const handleLiveListingsExport = () => {
    setLiveListingsExporting(true);
    try {
      if (!allLiveListings.length) {
        addToast('No listings loaded to export.', 'error');
        return;
      }
      downloadLiveListingsCsv(allLiveListings);
      addToast(
        `Exported all ${allLiveListings.length} listings (full catalog). Filter in Excel as needed.`,
        'success'
      );
    } catch (err) {
      addToast(err.message || 'CSV export failed', 'error');
    } finally {
      setLiveListingsExporting(false);
    }
  };

  const loadSubmissions = async () => {
    setLoadingSubs(true);
    try {
      const res = await apiClient.getSubmissions();
      if (res.data && Array.isArray(res.data)) {
        setSubmissions(res.data);
      }
    } catch (err) {
      addToast(err.message || "Could not load submissions.", "error");
    } finally {
      setLoadingSubs(false);
    }
  };

  const finalizeSubmissionUnpublish = async (apiRes, titleHint) => {
    if (!apiRes?.data?.propertyUnpublished) return;
    if (onPropertyCreated) await onPropertyCreated();
    const title = titleHint || apiRes?.data?.title || "Listing";
    addToast(`"${title}" was removed from the main Properties page and Live listings.`, "info");
  };

  const finalizeSubmissionPublish = async (id, status, apiRes, titleHint) => {
    if (!SUBMISSION_PUBLISH_STATUSES.includes(status)) {
      await finalizeSubmissionUnpublish(apiRes, titleHint);
      return;
    }
    let row = apiRes?.data;
    if (!row?.publishedPropertyId && !row?.propertyPublished) {
      try {
        const pub = await apiClient.publishSubmission(id);
        row = { ...row, ...pub?.data, propertyPublished: !pub?.data?.alreadyPublished };
      } catch (err) {
        addToast(
          "Pipeline saved, but the listing is not on the Properties page yet. Redeploy the API (see docs/DEPLOY_BACKEND.md) and refresh admin.",
          "error"
        );
        return;
      }
    }
    if (onPropertyCreated) await onPropertyCreated();
    const title = titleHint || row?.title || "Listing";
    if (row?.propertyPublished) {
      addToast(`"${title}" is now live on the main Properties page.`, "success");
    } else if (row?.publishedPropertyId || row?.propertyId) {
      addToast(`"${title}" is on the main Properties page.`, "success");
    }
  };

  const loadAdminProfile = async () => {
    try {
      const res = await apiClient.getMe();
      if (res.data?.hasAdminPin) {
        setHasAdminPin(true);
        return;
      }
    } catch (_) { /* fall through */ }
    try {
      const hash = await adminFirestoreGetPinHash();
      setHasAdminPin(!!hash);
    } catch (_) {
      setHasAdminPin(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadSubmissions();
      loadAdminProfile();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!pendingSubmissionReview || manageProperty) return;
    const { submissionId, queueFilter } = pendingSubmissionReview;
    setActiveTab("submissions");
    setSubmissionQueueFilter(queueFilter);
    setSubmissionListingModeFilter("all");
    setSubmissionSearchQuery("");
    setSubmissionDateMode("all");
    setSubmissionDateYear("");
    setSubmissionDateMonth("");
    setSubmissionDateDay("");
    setSelectedSubmissionId(submissionId);
    setFocusedSubmissionId(submissionId);
    setPendingSubmissionReview(null);
    requestAnimationFrame(() => {
      document.querySelector(".admin-tabs")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      document.querySelector(".admin-submission-detail")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [pendingSubmissionReview, manageProperty]);

  const patchSubmissionInState = (id, updates) => {
    setSubmissions((prev) => prev.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  };

  const saveAdminPin = async (pin) => {
    const normalized = assertValidAdminPin(pin);
    try {
      await apiClient.setAdminPin(normalized);
      setHasAdminPin(true);
      await loadAdminProfile();
      addToast("Approval PIN saved. Use this same PIN when you approve a listing.", "success");
      return normalized;
    } catch (err) {
      console.warn("[admin] API setAdminPin failed", err);
      throw new Error(
        err.message || "Could not save PIN. Deploy the API (./scripts/deploy-coown-api.sh) and try again."
      );
    }
  };

  const changeAdminPin = async (currentPin, newPin) => {
    const current = assertValidAdminPin(currentPin);
    const next = assertValidAdminPin(newPin);
    if (current === next) throw new Error("New PIN must be different from your current PIN.");
    await apiClient.changeAdminPin(current, next);
    setHasAdminPin(true);
    await loadAdminProfile();
    addToast("Approval PIN updated.", "success");
    return next;
  };

  const mergeOfferingDraftIntoBody = (id, base) => {
    const draft = offeringDraftRef.current[id];
    if (!draft) return base;
    return { ...base, ...draft };
  };

  const advanceSubmissionPipeline = async (id, status, adminPin) => {
    const label = getPipelineStepLabel(status);
    const notes = `Pipeline: ${label} (updated by admin)`;
    const normalizedPin = adminPin != null ? assertValidAdminPin(adminPin) : null;
    const body = mergeOfferingDraftIntoBody(id, { status, notes });
    if (normalizedPin) body.adminPin = normalizedPin;
    const subTitle = submissions.find((s) => s.id === id)?.title;

    patchSubmissionInState(id, { status, notes });
    try {
      const res = await apiClient.updateSubmission(id, body);
      addToast(`Timeline updated to "${label}". The realtor will see this step highlighted.`);
      await finalizeSubmissionPublish(id, status, res, subTitle);
      const prior = submissions.find((s) => s.id === id);
      if (!SUBMISSION_PUBLISH_STATUSES.includes(status) && !res?.data?.propertyUnpublished && prior?.publishedPropertyId) {
        try {
          const unpub = await apiClient.unpublishSubmission(id);
          await finalizeSubmissionUnpublish({ data: { propertyUnpublished: unpub?.data?.unpublished, title: subTitle } }, subTitle);
        } catch (unpubErr) {
          console.warn("[admin] unpublish fallback failed", unpubErr);
        }
      }
      await loadSubmissions();
      return;
    } catch (err) {
      const msg = err.message || "";
      console.warn("[admin] API pipeline update failed, trying Firestore", err);
      try {
        const fromStatus = submissions.find((s) => s.id === id)?.status;
        await adminPipelineViaFirestore(id, status, normalizedPin, fromStatus);
        addToast(`Timeline updated to "${label}" (saved directly).`);
        if (SUBMISSION_PUBLISH_STATUSES.includes(status)) {
          await finalizeSubmissionPublish(id, status, null, subTitle);
        } else {
          await finalizeSubmissionUnpublish({ data: { propertyUnpublished: true, title: subTitle } }, subTitle);
        }
        await loadSubmissions();
        return;
      } catch (fbErr) {
        console.error("[admin] Firestore pipeline failed", fbErr);
        await loadSubmissions();
        let detail = fbErr.message || msg || "Update failed.";
        if (/permission|insufficient/i.test(detail)) {
          detail = "Could not save (Firestore permission denied). Run: firebase deploy --only firestore:rules,hosting and redeploy the API.";
        }
        addToast(detail, "error");
        throw new Error(detail);
      }
    }
  };

  const runPipelineAction = async (fn) => {
    if (pipelineBusy) return;
    setPipelineBusy(true);
    try {
      await fn();
    } finally {
      setPipelineBusy(false);
    }
  };

  const handlePinModalSubmit = async (pinOrPayload, mode) => {
    if (mode === "change") {
      const { currentPin, newPin } = pinOrPayload || {};
      await changeAdminPin(currentPin, newPin);
      return;
    }
    const normalized = assertValidAdminPin(pinOrPayload);
    if (mode === "set" || mode === "reset") {
      await saveAdminPin(normalized);
      if (pendingApproval) {
        await advanceSubmissionPipeline(pendingApproval.id, pendingApproval.status, normalized);
        setPendingApproval(null);
      }
      return;
    }
    if (mode === "approve" || mode === "verify") {
      if (!pendingApproval) {
        throw new Error("No listing selected for approval.");
      }
      if (!hasAdminPin) {
        throw new Error("Set your approval PIN first (Set up PIN in the banner above).");
      }
      await advanceSubmissionPipeline(pendingApproval.id, pendingApproval.status, normalized);
      setPendingApproval(null);
      loadAdminProfile().catch(() => {});
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-page admin-gate">
        <div className="admin-gate-card">
          <div className="admin-gate-icon"><i className="fas fa-shield-halved"></i></div>
          <h2>Admin sign-in required</h2>
          <p style={{color:"var(--text-light)",margin:"0.75rem 0 1.5rem",lineHeight:1.6}}>Sign in with an admin account to open the control center.</p>
          <button type="button" className="btn btn-primary" onClick={onGoLogin}>Go to Log In</button>
        </div>
      </div>
    );
  }

  if (adminCheckPending) {
    return (
      <div className="admin-page admin-gate">
        <div className="admin-gate-card">
          <div className="admin-gate-icon"><i className="fas fa-spinner fa-spin"></i></div>
          <h2>Checking access</h2>
          <p style={{color:"var(--text-light)",marginTop:"0.75rem"}}>Verifying administrator permissions…</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-page admin-gate">
        <div className="admin-gate-card">
          <div className="admin-gate-icon danger"><i className="fas fa-lock"></i></div>
          <h2>Access denied</h2>
          <p style={{color:"var(--text-light)",margin:"0.75rem 0 1.5rem",lineHeight:1.6}}>
            Signed in as <strong>{userEmail || "your account"}</strong>. This account is not an administrator.
          </p>
          <button type="button" className="btn btn-outline" onClick={onGoHome}>Back to site</button>
        </div>
      </div>
    );
  }

  const adminTabs = [
    { id: "submissions", label: "Submissions", icon: "fa-inbox", count: submissions.length },
    { id: "add-property", label: "Add Property", icon: "fa-plus-circle" },
    { id: "deals", label: "Live Listings", icon: "fa-building", count: allLiveListings.length },
    { id: "deleted", label: "Deleted listings", icon: "fa-trash-alt", count: deletedCount },
    { id: "insights", label: "Insights", icon: "fa-newspaper" },
    { id: "legal", label: "Legal pages", icon: "fa-gavel" },
    { id: "overview", label: "Overview", icon: "fa-chart-line" },
    { id: "distributions", label: "Distributions", icon: "fa-hand-holding-usd" },
    { id: "kyc", label: "KYC Review", icon: "fa-user-check" },
    { id: "chat-reports", label: "Chat reports", icon: "fa-flag", count: chatReportsOpenCount > 0 ? chatReportsOpenCount : null },
    { id: "user-access", label: "User access", icon: "fa-user-slash" },
    { id: "documents", label: "Documents", icon: "fa-folder-open", count: documentsOpenCount > 0 ? documentsOpenCount : null },
  ];

  const totalInvested = investments.reduce((s,i)=>s+i.invested,0);
  const activeDeals = allLiveListings.filter((d) => d.status === "funding").length;
  const fundedDeals = allLiveListings.filter((d) => d.status === "funded").length;

  const submissionStats = {
    pending: submissions.filter((s) => classifySubmission(s.status) === "pending").length,
    approved: submissions.filter((s) => classifySubmission(s.status) === "approved").length,
    rejected: submissions.filter((s) => classifySubmission(s.status) === "rejected").length
  };

  const filteredSubmissions = [...submissions]
    .filter((s) => classifySubmission(s.status) === submissionQueueFilter)
    .filter((s) => submissionListingModeFilter === "all" || getSubmissionListingMode(s) === submissionListingModeFilter)
    .filter((s) => submissionMatchesSearch(s, submissionSearchQuery))
    .filter((s) => submissionMatchesAdminDateFilter(s, submissionDateFilter))
    .sort((a, b) => submissionSortTime(b) - submissionSortTime(a));

  const submissionModeCounts = {
    sale: submissions.filter((s) => classifySubmission(s.status) === submissionQueueFilter && getSubmissionListingMode(s) === "sale").length,
    rent: submissions.filter((s) => classifySubmission(s.status) === submissionQueueFilter && getSubmissionListingMode(s) === "rent").length
  };

  const effectiveSelectedId = (() => {
    if (focusedSubmissionId && submissions.some((s) => s.id === focusedSubmissionId)) {
      return focusedSubmissionId;
    }
    if (selectedSubmissionId && filteredSubmissions.some((s) => s.id === selectedSubmissionId)) {
      return selectedSubmissionId;
    }
    return filteredSubmissions[0]?.id || null;
  })();

  const selectedSubmission = effectiveSelectedId
    ? submissions.find((s) => s.id === effectiveSelectedId) || null
    : null;


  const renderSubmissionRow = (sub) => (
    <button
      key={sub.id}
      type="button"
      role="option"
      aria-selected={effectiveSelectedId === sub.id}
      className={`submission-row${effectiveSelectedId === sub.id ? " active" : ""}`}
      onClick={() => {
        setFocusedSubmissionId(null);
        setSelectedSubmissionId(sub.id);
      }}
    >
      <div
        className="submission-row-thumb"
        style={sub.images?.[0] ? { backgroundImage: `url(${sub.images[0]})` } : undefined}
      >
        {!sub.images?.[0] && <i className="fas fa-home" aria-hidden="true"></i>}
      </div>
      <div className="submission-row-body">
        <div className="submission-row-title-row">
          <span className="submission-row-title-text" title={sub.title || "Untitled"}>{sub.title || "Untitled"}</span>
          <span className={`status-badge listing-mode-badge status-${getSubmissionListingMode(sub) === "rent" ? "pending" : "approved"}`}>
            {getSubmissionListingMode(sub) === "rent" ? "Rent" : "Sale"}
          </span>
        </div>
        <div className="submission-row-meta" title={`${sub.location || ""} · $${Number(sub.price || 0).toLocaleString()}`}>
          <span>{sub.submittedDate || "—"}</span>
          {sub.location ? <span> · {sub.location}</span> : null}
          <span> · ${Number(sub.price || 0).toLocaleString()}</span>
        </div>
      </div>
      <div className="submission-row-status">
        <span className={`status-badge status-${submissionStatusBadgeClass(sub.status)}`}>
          {formatSubmissionStatusLabel(sub.status)}
        </span>
      </div>
    </button>
  );

  const openApprovalPinModal = (id, status) => {
    setPendingApproval({ id, status: status || "approved" });
    setPinModal({ open: true, mode: "approve" });
  };

  const handlePublishSubmission = async (id) => {
    if (publishBusy) return;
    const sub = submissions.find((s) => s.id === id);
    if (saleListingNeedsRaisingApproval(sub)) {
      addToast("Approve the share structure (Save & approve) before publishing to the main page.", "error");
      return;
    }
    setPublishBusy(true);
    try {
      const subTitle = sub?.title;
      const offeringBody = offeringDraftRef.current[id];
      if (offeringBody) {
        await apiClient.updateSubmission(id, offeringBody);
      }
      const pub = await apiClient.publishSubmission(id);
      await finalizeSubmissionPublish(id, normalizePipelineStatus(submissions.find((s) => s.id === id)?.status || "approved"), { data: { ...pub?.data, title: subTitle, propertyPublished: !pub?.data?.alreadyPublished } }, subTitle);
      await loadSubmissions();
    } catch (err) {
      addToast(err.message || "Could not publish listing.", "error");
    } finally {
      setPublishBusy(false);
    }
  };

  const handleSaveRaisingStructure = async (id, body) => {
    if (raisingSaveBusy) return;
    setRaisingSaveBusy(true);
    try {
      const res = await apiClient.updateSubmission(id, body);
      const updated = res?.data;
      if (updated) patchSubmissionInState(id, updated);
      addToast("Share structure approved and saved. You can move this listing Live on Site when ready.");
      if (onPropertyCreated) await onPropertyCreated();
      await loadSubmissions();
    } catch (err) {
      addToast(err.message || "Could not save share structure.", "error");
    } finally {
      setRaisingSaveBusy(false);
    }
  };

  const handleOfferingDraftChange = (id, body) => {
    if (id && body) offeringDraftRef.current[id] = body;
  };

  const handleSaveSubmissionContent = async (id, body) => {
    if (contentSaveBusy) return;
    setContentSaveBusy(true);
    try {
      const res = await apiClient.updateSubmission(id, body);
      const updated = res?.data;
      if (updated) patchSubmissionInState(id, updated);
      addToast('Listing details saved.');
      if (onPropertyCreated) await onPropertyCreated();
      await loadSubmissions();
    } catch (err) {
      addToast(err.message || 'Could not save listing details.', 'error');
    } finally {
      setContentSaveBusy(false);
    }
  };

  const handleSaveOfferingDetails = async (id, body) => {
    if (offeringSaveBusy) return;
    setOfferingSaveBusy(true);
    try {
      const res = await apiClient.updateSubmission(id, body);
      const updated = res?.data;
      if (updated) patchSubmissionInState(id, updated);
      if (body) offeringDraftRef.current[id] = body;
      addToast("Offering details saved.");
      if (onPropertyCreated) await onPropertyCreated();
      await loadSubmissions();
    } catch (err) {
      addToast(err.message || "Could not save offering details.", "error");
    } finally {
      setOfferingSaveBusy(false);
    }
  };

  const pipelineHandlers = {
    onAdvance: (id, status) => runPipelineAction(() => advanceSubmissionPipeline(id, status)),
    onReject: (id) => {
      if (!window.confirm("Reject this listing?")) return;
      runPipelineAction(() => advanceSubmissionPipeline(id, "rejected"));
    },
    onRequestApprovePin: openApprovalPinModal,
    onPublishToSite: handlePublishSubmission,
    onSaveRaisingStructure: handleSaveRaisingStructure,
    onSaveOfferingDetails: handleSaveOfferingDetails,
    onSaveSubmissionContent: handleSaveSubmissionContent,
    onOfferingDraftChange: handleOfferingDraftChange,
    raisingSaveBusy,
    offeringSaveBusy,
    contentSaveBusy,
    publishBusy,
    addToast
  };

  return (
    <div className="admin-page">
      <AdminPinModal
        open={pinModal.open}
        mode={pinModal.mode}
        onClose={() => { setPinModal({ open: false, mode: "set" }); setPendingApproval(null); }}
        onSubmit={handlePinModalSubmit}
        addToast={addToast}
      />
      <header className="admin-hero">
        <div className="admin-hero-inner">
          <div className="admin-hero-brand">
            <div className="admin-hero-mark" aria-hidden="true">
              <i className="fas fa-shield-halved"></i>
            </div>
            <div>
              <span className="admin-eyebrow">CoOwn Control Center</span>
              <h1>Admin Dashboard</h1>
              <p className="admin-hero-desc">
                Review submissions, publish listings, and manage platform activity.
              </p>
              <p className="admin-hero-meta">build {COOWN_ADMIN_BUILD}</p>
            </div>
          </div>
          <button type="button" className="btn btn-outline" onClick={onGoHome}>
            <i className="fas fa-arrow-left"></i> Back to site
          </button>
        </div>
      </header>
      <div className="admin-body">
        {isAdmin && !hasAdminPin && (
          <div className="admin-alerts">
            <div className="admin-alert admin-alert--warn">
              <div className="admin-alert-content">
                <div className="admin-alert-icon"><i className="fas fa-key"></i></div>
                <div>
                  <p className="admin-alert-title">Create your approval PIN</p>
                  <p className="admin-alert-text">
                    Required for new admins. Use 4–6 digits when you approve a listing (Price Agreement → Approved).
                  </p>
                </div>
              </div>
              <div className="admin-alert-actions">
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setPinModal({ open: true, mode: "set" })}>
                  Create PIN
                </button>
              </div>
            </div>
          </div>
        )}
        {isAdmin && hasAdminPin && (
          <div className="admin-alerts">
            <div className="admin-alert admin-alert--pin">
              <div className="admin-alert-content">
                <div className="admin-alert-icon"><i className="fas fa-lock"></i></div>
                <div>
                  <p className="admin-alert-title">Approval PIN is active</p>
                  <p className="admin-alert-text">
                    Wrong PIN is rejected. Use Reset if you forgot it, or Change if you know the current PIN.
                  </p>
                </div>
              </div>
              <div className="admin-alert-actions">
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setPinModal({ open: true, mode: "change" })}>
                  Change PIN
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm admin-btn-reset"
                  onClick={() => {
                    if (!window.confirm("Reset your approval PIN? You will choose a new PIN without entering the old one.")) return;
                    setPinModal({ open: true, mode: "reset" });
                  }}
                >
                  Reset PIN
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="admin-stats">
          <div className="admin-stat">
            <div className="admin-stat-icon green"><i className="fas fa-building"></i></div>
            <div><label>Total Deals</label><value>{allLiveListings.length}</value></div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat-icon gold"><i className="fas fa-chart-line"></i></div>
            <div><label>Active Funding</label><value>{activeDeals}</value></div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat-icon blue"><i className="fas fa-check-circle"></i></div>
            <div><label>Fully Funded</label><value>{fundedDeals}</value></div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat-icon red"><i className="fas fa-dollar-sign"></i></div>
            <div><label>Total Invested</label><value>${(totalInvested / 1000000).toFixed(1)}M</value></div>
          </div>
        </div>
        <nav className="admin-tabs" aria-label="Admin sections">
          {adminTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`admin-tab${activeTab === t.id ? " active" : ""}`}
              onClick={() => setActiveTab(t.id)}
            >
              <i className={`fas ${t.icon}`}></i> {t.label}
              {t.count != null && t.count > 0 && <span className="admin-tab-badge">{t.count}</span>}
            </button>
          ))}
        </nav>
        <div className="admin-panel">
          {activeTab === "submissions" && (
            <div>
              <div className="admin-panel-head">
                <div>
                  <h3><i className="fas fa-inbox"></i> Listing review queue</h3>
                  <p className="admin-panel-sub">
                    Newest first · select a row to review details and advance the pipeline
                  </p>
                </div>
                <button type="button" className="btn btn-outline btn-sm" onClick={loadSubmissions} disabled={loadingSubs}>
                  <i className={`fas fa-sync${loadingSubs ? " fa-spin" : ""}`}></i> Refresh
                </button>
              </div>
              <div className="submission-queue-stats">
                {[
                  { key: "pending", label: "Pending / in review", icon: "fa-hourglass-half" },
                  { key: "approved", label: "Approved & live", icon: "fa-check-circle" },
                  { key: "rejected", label: "Rejected", icon: "fa-times-circle" }
                ].map((box) => (
                  <button
                    key={box.key}
                    type="button"
                    className={`submission-queue-stat${submissionQueueFilter === box.key ? " active" : ""}`}
                    onClick={() => setSubmissionQueueFilter(box.key)}
                  >
                    <label><i className={`fas ${box.icon}`}></i> {box.label}</label>
                    <value>{submissionStats[box.key]}</value>
                  </button>
                ))}
              </div>
              {loadingSubs && submissions.length === 0 ? (
                <p style={{color:"var(--text-light)",textAlign:"center",padding:"2rem"}}><i className="fas fa-spinner fa-spin"></i> Loading submissions…</p>
              ) : submissions.length === 0 ? (
                <div className="admin-empty">
                  <i className="fas fa-inbox"></i>
                  <p>No submissions yet.</p>
                </div>
              ) : (
                <div className="submission-queue-layout">
                  <div className="submission-queue-sidebar">
                    <div className="submission-queue-toolbar">
                      <div className="submission-queue-search">
                        <div className="submission-queue-search-wrap">
                          <i className="fas fa-search" aria-hidden="true"></i>
                          <input
                            type="text"
                            inputMode="search"
                            autoComplete="off"
                            spellCheck={false}
                            placeholder="Search title, city, agent…"
                            value={submissionSearchQuery}
                            onChange={(e) => setSubmissionSearchQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                            aria-label="Search submissions"
                          />
                          {submissionSearchQuery.trim() ? (
                            <button
                              type="button"
                              className="submission-queue-search-clear"
                              aria-label="Clear search"
                              onClick={() => setSubmissionSearchQuery("")}
                            >
                              <i className="fas fa-times" aria-hidden="true"></i>
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="submission-mode-filters" role="tablist" aria-label="Listing type">
                        {[
                          { key: "all", label: "All" },
                          { key: "sale", label: `For sale (${submissionModeCounts.sale})` },
                          { key: "rent", label: `For rent (${submissionModeCounts.rent})` }
                        ].map((tab) => (
                          <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={submissionListingModeFilter === tab.key}
                            className={`submission-mode-filter${submissionListingModeFilter === tab.key ? " active" : ""}`}
                            onClick={() => setSubmissionListingModeFilter(tab.key)}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                      <BrowseListedDateFilter
                        variant="admin"
                        useCalendarInputs
                        fieldLabel="Submitted"
                        yearMin={COOWN_PROJECT_START_YEAR}
                        dateMode={submissionDateMode}
                        onDateModeChange={setSubmissionDateMode}
                        listedYear={submissionDateYear}
                        onListedYearChange={setSubmissionDateYear}
                        listedMonth={submissionDateMonth}
                        onListedMonthChange={setSubmissionDateMonth}
                        listedDate={submissionDateDay}
                        onListedDateChange={setSubmissionDateDay}
                        onClear={() => {
                          setSubmissionDateMode("all");
                          setSubmissionDateYear("");
                          setSubmissionDateMonth("");
                          setSubmissionDateDay("");
                        }}
                      />
                    </div>
                    <div className="submission-queue-list-panel">
                      <div className="submission-queue-list" role="listbox" aria-label="Submissions">
                        {filteredSubmissions.length === 0 ? (
                          <div className="submission-queue-list-empty">
                            <i className="fas fa-search" aria-hidden="true"></i>
                            <p>
                              {submissionSearchQuery.trim()
                                ? `No listings match "${submissionSearchQuery.trim()}". Try another keyword or clear search.`
                                : "No listings in this queue filter. Try another tab above."}
                            </p>
                          </div>
                        ) : submissionListingModeFilter === "all" ? (
                          [
                            { mode: "sale", label: "For sale — fractional shares" },
                            { mode: "rent", label: "For rent — rental listings" }
                          ].map((section) => {
                            const sectionItems = filteredSubmissions.filter((s) => getSubmissionListingMode(s) === section.mode);
                            if (!sectionItems.length) return null;
                            return (
                              <div key={section.mode} className="submission-queue-section">
                                <div className="submission-queue-section-label">{section.label} ({sectionItems.length})</div>
                                {sectionItems.map((sub) => renderSubmissionRow(sub))}
                              </div>
                            );
                          })
                        ) : (
                          filteredSubmissions.map((sub) => renderSubmissionRow(sub))
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="submission-queue-detail">
                    {selectedSubmission ? (
                      <AdminSubmissionDetail sub={selectedSubmission} {...pipelineHandlers} pipelineBusy={pipelineBusy} />
                    ) : (
                      <div className="admin-empty">
                        <i className="fas fa-hand-pointer"></i>
                        <p>
                          {submissionSearchQuery.trim() && filteredSubmissions.length === 0
                            ? "Adjust your search to preview a listing."
                            : "Select a listing from the list."}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === "add-property" && (
        <div>
          <div className="admin-panel-head">
            <div>
              <h3><i className="fas fa-plus-circle"></i> Publish a new property</h3>
              <p className="admin-panel-sub">
                Matches the public lister form (photos, video, nearby, rent costs, offering details) plus admin controls for platform price and featured listings. Choose fractional sale or rent before publishing.
              </p>
            </div>
          </div>
          <AdminAddPropertyPanel
            addToast={addToast}
            userEmail={userEmail}
            onPublished={async () => {
              if (onPropertyCreated) await onPropertyCreated();
              setActiveTab("deals");
            }}
          />
        </div>
      )}
          {activeTab === "overview" && <AdminPlatformOverview addToast={addToast} />}
          {activeTab === "distributions" && (
            <AdminDistributionPanel catalogProperties={catalogProperties} addToast={addToast} />
          )}
          {activeTab === "insights" && <AdminInsightsPanel addToast={addToast} />}
          {activeTab === "legal" && <AdminLegalPagesPanel addToast={addToast} />}
          {activeTab === "kyc" && <AdminKycReviewPanel addToast={addToast} />}
          {activeTab === "chat-reports" && (
            <AdminChatReportsPanel addToast={addToast} onQueueChange={refreshChatReportCount} />
          )}
          {activeTab === "user-access" && <AdminUserAccessPanel addToast={addToast} standalone />}
          {activeTab === "documents" && (
            <AdminPropertyDocumentsPanel addToast={addToast} onQueueChange={refreshDocumentQueueCount} />
          )}
          {activeTab === "deleted" && (
            <AdminDeletedListingsPanel
              addToast={addToast}
              onCountChange={setDeletedCount}
              onRestored={async () => {
                if (onPropertyCreated) await onPropertyCreated();
                await loadSubmissions();
              }}
            />
          )}
          {activeTab === "deals" && (
        <div>
          <div className="admin-panel-head">
            <div>
              <h3><i className="fas fa-building"></i> Live listings</h3>
              <p className="admin-panel-sub">
                All countries — demo samples plus real Firestore listings on the public site. Only Firestore rows can be edited or removed here.
                CSV export includes the <strong>full catalog</strong> for analytics (table filters are for browsing only).
              </p>
            </div>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={handleLiveListingsExport}
              disabled={liveListingsExporting || allLiveListings.length === 0}
              title="Download every live and demo listing for Excel analysis"
            >
              <i className={`fas fa-download${liveListingsExporting ? ' fa-spin' : ''}`}></i> Download CSV (all)
            </button>
          </div>
          <div className="platform-activity-layout live-listings-analytics">
            <div className="detail-metrics platform-activity-metrics">
              {LIVE_LISTINGS_METRICS.map((metric) => (
                <div key={metric.key} className="detail-metric">
                  <label>{metric.label}</label>
                  <value>{Number(liveListingsMetrics[metric.key] || 0).toLocaleString()}</value>
                </div>
              ))}
            </div>
            <PlatformActivityChart
              metrics={liveListingsMetrics}
              metricDefs={LIVE_LISTINGS_METRICS}
              chartType={liveListingsChartType}
              onChartTypeChange={setLiveListingsChartType}
              title="Catalog breakdown"
              emptyMessage="No listings match the current filters."
              ariaLabel="Live listings breakdown"
              colorMap={LIVE_LISTINGS_CHART_COLORS}
            />
          </div>
          {liveListingsFiltersActive ? (
            <p className="live-listings-analytics-note">
              Metrics and chart reflect <strong>{filteredLiveListings.length.toLocaleString()}</strong> filtered listings.
              CSV export still includes the full catalog ({allLiveListings.length.toLocaleString()}).
            </p>
          ) : null}
          <div className="live-listings-toolbar" aria-label="Browse live listings">
            <form
              className="live-listings-toolbar__search submission-queue-search"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className="submission-queue-search-wrap">
                <i className="fas fa-search" aria-hidden="true"></i>
                <input
                  type="search"
                  placeholder="Search title, location, type, manager, status…"
                  value={liveListingsSearch}
                  onChange={(e) => setLiveListingsSearch(e.target.value)}
                  aria-label="Search live listings"
                />
                {liveListingsSearch ? (
                  <button
                    type="button"
                    className="submission-queue-search-clear"
                    aria-label="Clear search"
                    onClick={() => setLiveListingsSearch("")}
                  >
                    <i className="fas fa-times" aria-hidden="true"></i>
                  </button>
                ) : null}
              </div>
            </form>
            <div className="live-listings-toolbar__grid">
              <div className="live-listings-toolbar__group">
                <span className="live-listings-toolbar__label">Listing type</span>
                <div className="listings-view-toggle" role="tablist" aria-label="Live listing type">
                  {[
                    { key: "all", label: `All (${liveListingModeCounts.all})` },
                    { key: "sale", label: `Fractional buy (${liveListingModeCounts.sale})` },
                    { key: "rent", label: `For rent (${liveListingModeCounts.rent})` },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={liveListingsModeFilter === tab.key}
                      className={liveListingsModeFilter === tab.key ? "active" : ""}
                      onClick={() => setLiveListingsModeFilter(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <BrowseListedDateFilter
                variant="admin"
                fieldLabel="Listed"
                yearMin={COOWN_PROJECT_START_YEAR}
                useCalendarInputs
                dateMode={liveListingsDateMode}
                onDateModeChange={setLiveListingsDateMode}
                listedYear={liveListingsYear}
                onListedYearChange={setLiveListingsYear}
                listedMonth={liveListingsMonth}
                onListedMonthChange={setLiveListingsMonth}
                listedDate={liveListingsDate}
                onListedDateChange={setLiveListingsDate}
                onClear={() => {
                  setLiveListingsYear("");
                  setLiveListingsMonth("");
                  setLiveListingsDate("");
                }}
              />
            </div>
          </div>
          <div className="admin-table-wrap">
            <table className="investment-table investment-table--live-listings">
              <thead>
                <tr><th>Deal</th><th>Listing</th><th>Type</th><th>Target</th><th>Raised</th><th>Status</th><th>Manager</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {allLiveListings.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: "2rem", color: "var(--text-light)" }}>
                      No listings loaded. Refresh or check the API connection.
                    </td>
                  </tr>
                ) : null}
                {allLiveListings.length > 0 && liveListingsTotal === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: "center", padding: "2rem", color: "var(--text-light)" }}>
                      No listings match your filters.
                    </td>
                  </tr>
                ) : null}
                {liveListingsPage.map(d => {
                  const isRent = getPropertyListingMode(d) === "rent";
                  const progress = d.shares ? ((d.sharesSold / d.shares) * 100).toFixed(1) : "0";
                  const isLive = isFirestoreListingId(d.id);
                  const pm = propertyManagerFromRecord(d);
                  const listedAt = getPropertyListedAt(d);
                  const listedLabel = listedAt
                    ? listedAt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
                    : null;
                  const dealMeta = [d.location, listedLabel ? `Listed ${listedLabel}` : null].filter(Boolean).join(" · ");
                  const listerPrice = !isRent ? getPropertyListerPrice(d) : 0;
                  const platformPrice = !isRent ? getPropertyPlatformPrice(d) : 0;
                  const showListerSubline = listerPrice > 0 && platformPrice > listerPrice;
                  return (
                    <tr key={d.id}>
                      <td className="live-listing-deal-cell">
                        <div className="live-listing-deal-line">
                          <strong className="live-listing-deal-title">{d.title}</strong>
                          <span className={`admin-listing-badge live-listing-deal-badge ${isLive ? "admin-listing-badge--live" : "admin-listing-badge--demo"}`}>
                            {isLive ? "Live" : "Demo"}
                          </span>
                        </div>
                        {dealMeta ? <div className="live-listing-deal-sub">{dealMeta}</div> : null}
                      </td>
                      <td>
                        <span className={`status-badge listing-mode-badge status-${isRent ? "pending" : "approved"}`}>
                          {formatSubmissionListingModeLabel(d, { short: true })}
                        </span>
                      </td>
                      <td>{d.type}</td>
                      <td className="live-listing-target-cell">
                        {isRent && Number(d.monthlyRent) > 0 ? (
                          <span className="live-listing-target-main">{formatUsd(Number(d.monthlyRent))}/mo</span>
                        ) : (
                          <>
                            <span className="live-listing-target-main">{formatExactMoney(platformPrice, d.country || "US")}</span>
                            {showListerSubline ? (
                              <span className="live-listing-target-sub">Lister {formatUsd(listerPrice)}</span>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td>{isRent ? "—" : `${progress}%`}</td>
                      <td><span className={`status-badge status-${d.status}`}>{d.status.replace(/\b\w/g, l => l.toUpperCase())}</span></td>
                      <td>{pm.name || "—"}</td>
                      <td>
                        <button type="button" className="btn btn-outline btn-sm live-listing-manage-btn" onClick={() => setManageProperty(d)}>Manage</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {allLiveListings.length > 0 && (
            <PaginationBar
              page={liveListingsPageNum}
              totalPages={liveListingsTotalPages}
              total={liveListingsTotal}
              startIndex={liveListingsStart}
              endIndex={liveListingsEnd}
              hasPrev={liveListingsHasPrev}
              hasNext={liveListingsHasNext}
              onPageChange={goToLiveListingsPage}
              itemLabel="listings"
              compact
            />
          )}
        </div>
      )}
        </div>
      </div>
      {manageProperty && (
        <AdminPropertyManageModal
          property={manageProperty}
          submissions={submissions}
          onClose={() => setManageProperty(null)}
          onSaved={async () => {
            if (onPropertyCreated) await onPropertyCreated();
            await loadSubmissions();
            await loadDeletedSubmissions();
          }}
          onOpenSubmission={(submissionId) => {
            const sub = submissions.find((s) => s.id === submissionId);
            if (!sub) {
              addToast("Linked submission not found. Refresh the admin page and try again.", "error");
              return;
            }
            setPendingSubmissionReview({
              submissionId,
              queueFilter: classifySubmission(sub.status),
            });
            setManageProperty(null);
          }}
          addToast={addToast}
        />
      )}
    </div>
  );
};

export default AdminDashboardPage;
